import { Component, computed, inject, input, InputSignal, output, Signal } from '@angular/core';
import { TaskDetailsViewSettingsService } from '@app/components/task/task-splitview-list/task-details-content/task-details-view-settings.service';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { TaskParticipantsDataService } from '@app/shared/services/task-participants-data.service';
import { TaskTypesDataService } from '@app/shared/services/task-types-data.service';
import { TaskPriority } from '@app/shared/types/enums/task-priority.enum';
import { TaskStatus } from '@app/shared/types/enums/task-status.enum';
import { Project } from '@app/shared/types/models/project/project.model';
import { TaskEstimation } from '@app/shared/types/models/task/task-estimation';
import { TaskType } from '@app/shared/types/models/task/task-type.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiDividerComponent } from '@app/shared/ui/divider/ui-divider.component';

import { TaskDetailsMetadataVisibility } from './task-details-metadata.types';
import { TaskDetailsMobileMetadataComponent } from './task-details-mobile-metadata/task-details-mobile-metadata.component';
import { TaskDetailsWideMetadataComponent } from './task-details-wide-metadata/task-details-wide-metadata.component';

/** Shared metadata view controller. Reads stay here; route-owned Actions retain operations across remounts. */
@Component({
  selector: 'proffeo-task-details-metadata-panel',
  templateUrl: './task-details-metadata-panel.component.html',
  host: {
    class: 'block min-w-0 max-w-full'
  },
  imports: [UiDividerComponent, TaskDetailsMobileMetadataComponent, TaskDetailsWideMetadataComponent]
})
export class TaskDetailsMetadataPanelComponent {
  private readonly tagsService = inject(TagsDataService);
  private readonly taskParticipantsService = inject(TaskParticipantsDataService);
  private readonly taskTypesDataService = inject(TaskTypesDataService);
  private readonly taskActions = inject(TasksActionsService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly viewSettings = inject(TaskDetailsViewSettingsService);

  protected readonly activeProject: Signal<Project> = this.activeProjectService.activeProject;
  protected readonly activeCompanyTaskTypes: Signal<TaskType[]> = this.taskTypesDataService.getActiveCompanyTaskTypes();
  protected readonly taskParticipants = computed(
    () => this.taskParticipantsService.getTaskParticipants(this.task().id)() ?? []
  );
  protected readonly taskUsers = computed(() => this.taskParticipants()?.map(tp => tp.projectParticipant()) || []);
  protected readonly tags = computed(() => this.tagsService.getTaskTags(this.task().id)());
  protected readonly mobileVisibility = computed<TaskDetailsMetadataVisibility>(() => ({
    status: this.viewSettings.statusVisible(),
    progress: this.viewSettings.progressVisible(),
    priority: this.viewSettings.priorityVisible(),
    location: this.viewSettings.locationVisible(),
    startDate: this.viewSettings.startDateVisible(),
    endDate: this.viewSettings.endDateVisible(),
    industry: this.viewSettings.industryVisible(),
    totalHours: this.viewSettings.totalHoursVisible(),
    users: this.viewSettings.usersVisible(),
    tags: this.viewSettings.tagsVisible(),
    estimationTime: this.viewSettings.estimationTimeVisible(),
    estimationCost: this.viewSettings.estimationCostVisible(),
    estimationQuantity: this.viewSettings.estimationQuantityVisible(),
    acknowledgement: this.viewSettings.acknowledgementVisible()
  }));

  public readonly openTimeReportRequested = output<Task>();
  public readonly timeReportsTabRequested = output<void>();

  public readonly task: InputSignal<Task> = input.required<Task>();
  /** Compact reuses grouped fields and visibility settings with desktop calendar popovers. */
  public readonly layout = input.required<'mobile' | 'compact' | 'wide'>();

  protected onStatusSelected(status: TaskStatus): void {
    this.taskActions.updateTaskStatus(this.task(), status);
  }

  protected onPrioritySelected(priority: TaskPriority): void {
    this.taskActions.updateTaskPriority(this.task(), priority);
  }

  protected onTaskStartDateSelected(startDate: Date | null): void {
    if (startDate) {
      this.taskActions.updateTaskStartDate(this.task(), startDate);
    }
  }

  protected onTaskEndDateSelected(endDate: Date | null): void {
    if (endDate) {
      this.taskActions.updateTaskEndDate(this.task(), endDate);
    }
  }

  protected onPercentageChange(percentage: number): void {
    this.taskActions.updateTaskProgress(this.task(), percentage);
  }

  protected onIndustryClicked(): void {
    this.taskActions.openSelectIndustryDialog(this.task());
  }

  protected onLocationClicked(): void {
    this.taskActions.openSelectLocationDialog(this.task());
  }

  protected onEstimationSelected(estimation: TaskEstimation): void {
    this.taskActions.updateTaskEstimation(this.task(), estimation);
  }

  protected onChangeGroupClicked(): void {
    this.taskActions.openSelectGroupDialog(this.task());
  }

  protected onChangeParticipantsClicked(): void {
    this.taskActions.openSelectParticipantsDialog(this.task());
  }

  protected openTimeReportClicked(): void {
    this.openTimeReportRequested.emit(this.task());
  }

  protected timeReportsTabClicked(): void {
    this.timeReportsTabRequested.emit();
  }

  protected openTaskTagsEditor(): void {
    this.taskActions.openTaskTagsEditor(this.task());
  }
}
